/**
 * What a walkthrough records: the screen and the narration, or the narration
 * alone (TOADO-380).
 *
 * An audio walkthrough is not a degraded one, and the reason is the pins.
 * Every marked moment stores a full screenshot and the element it was about,
 * captured through the tab rather than through the recording stream, so an
 * audio session still files a ticket with its picture, its element, its
 * transcript and its description. What it gives up is motion. What it buys is
 * roughly forty times less to upload.
 */
export type RecordingMediaMode = 'video' | 'audio';

/**
 * How much data a walkthrough is allowed to cost (TOADO-379).
 *
 * Nothing used to say. `getDisplayMedia` was called with a bare `video: true`
 * and `MediaRecorder` was constructed with no bitrate, no frame-rate cap and
 * no resolution ceiling, so Chrome picked its own defaults for a
 * full-resolution screen. Measured across four of Jason's real walkthroughs on
 * 2026-09-20: 9 to 14 MB per minute, around 1.2 to 2 Mbps. A twenty-minute
 * session is then a few hundred megabytes, on a display whose size decides how
 * many.
 *
 * A screen recording of a mostly-static page being talked over is the easiest
 * encode there is, so these are deliberately well under what Chrome would
 * choose. Capping the resolution matters as much as the bitrate: it makes the
 * cost per minute the same on a laptop and on a 5K display, which is what
 * turns "it depends" into a number somebody can plan around.
 *
 * All four are hints rather than guarantees. A source that cannot be scaled or
 * re-timed is still accepted: a recording that happens at the wrong size beats
 * one that refuses to start.
 */

/** Long edge. 1080p is more than enough to read a page back. */
export const RECORDING_MAX_WIDTH = 1920;
export const RECORDING_MAX_HEIGHT = 1080;

/**
 * Enough to show a scroll, a click and a cursor. Screencasts have run at this
 * for years; the walkthrough is narration over a page, not gameplay.
 */
export const RECORDING_MAX_FRAME_RATE = 15;

/**
 * Text and flat colour at 15fps. Generous for the content, roughly half of
 * what was being spent before it.
 */
export const RECORDING_VIDEO_BITS_PER_SECOND = 800_000;

/** Speech. Opus is transparent for a single voice well below this. */
export const RECORDING_AUDIO_BITS_PER_SECOND = 64_000;

/**
 * The `MediaRecorder` options, as one object so the two recorders cannot
 * disagree. The mime type is the caller's: it is chosen by probing what this
 * browser supports.
 */
export function recorderOptions(mimeType: string): MediaRecorderOptions {
  return {
    ...(mimeType ? { mimeType } : {}),
    videoBitsPerSecond: RECORDING_VIDEO_BITS_PER_SECOND,
    audioBitsPerSecond: RECORDING_AUDIO_BITS_PER_SECOND,
  };
}

/**
 * The display constraints for the standard path, where the browser hands over
 * a stream in answer to a request.
 *
 * `ideal` rather than `exact` throughout: a source that cannot meet these
 * should give what it can, not fail.
 */
export function displayConstraints(): MediaStreamConstraints {
  return {
    video: {
      width: { max: RECORDING_MAX_WIDTH },
      height: { max: RECORDING_MAX_HEIGHT },
      frameRate: { ideal: RECORDING_MAX_FRAME_RATE, max: RECORDING_MAX_FRAME_RATE },
    },
    audio: false,
  };
}
